import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { Button } from '@mui/material';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApTooltip,
    PortalCustomIcon,
} from '@uipath/portal-shell-react';
import * as React from 'react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

interface CardProps {
    title: string;
    description: string;
    icon: string;
    onClick?: () => void;
}

export const UiServiceCard: React.FC<CardProps> = ({
    title, description, icon, onClick,
}) => {
    const { formatMessage: translate } = useIntl();
    const translatedDescription = translate({ id: description });

    const handleClick = useCallback(() => {
        onClick?.();
    }, [ onClick ]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    }, [ handleClick ]);

    return (
        <Button
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            aria-label={translate({ id: 'CLIENT_SERVICE_CARD_OPEN' }, { title })}
            type="button"
            tabIndex={0}
            disableRipple
            sx={{
                background: 'none',
                width: '100%',
                minWidth: '190px',
                height: '130px',
                border: '1px solid var(--color-border-de-emp)',
                borderRadius: '8px',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
                position: 'relative',
                outline: 'none',
                textTransform: 'none',
                '&:hover': {
                    background: 'none',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                },
                '&:focus': {
                    background: 'none',
                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    outline: '2px solid var(--color-focus-indicator)',
                },
            }}
        >
            <UiStack
                w="100%"
                p={SpacingToken.Base}
                direction="column"
                gap={SpacingToken.L}>
                <UiStack
                    direction="row"
                    justify="between"
                    align="center"
                >
                    <UiText
                        variant={FontVariantToken.fontSizeH4Bold}
                        aria-level={3}
                        color="var(--color-foreground)">
                        {title}
                    </UiText>
                    <PortalCustomIcon
                        size='18px'
                        name={icon} />
                </UiStack>

                <ApTooltip
                    content={translatedDescription}
                    placement="bottom"
                    smartTooltip
                >
                    <div
                        style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            height: '50px',
                        }}
                        tabIndex={0}
                        role="button"
                    >
                        <UiText
                            variant={FontVariantToken.fontSizeS}
                            color="var(--color-foreground)">
                            {translatedDescription}
                        </UiText>
                    </div>
                </ApTooltip>
            </UiStack>
        </Button>
    );
};
